/**
 * What people write in Messages, read for showing: words (with links and @mentions, in
 * Messages.tsx) and code. Code is what's between ``` fences (a block) or `single backticks` (inline),
 * as people type it in chat apps; pasting something that looks like code wraps it in a fence for
 * them. Everything here is plain text in, plain data out: nothing is ever shown as HTML.
 *
 * The server reads code the same way when it picks the address to preview
 * (apps/api/src/messaging/previews.ts, withoutCode): keep the two in step.
 */

export type Block = { kind: 'text'; text: string } | { kind: 'code'; code: string; lang: string | null };
export type Inline = { kind: 'text'; text: string } | { kind: 'code'; code: string };

const FENCE_OPEN = /^\s*```(.*)$/;
const LANG = /^[A-Za-z0-9_+#.-]{1,20}$/;

/**
 * A message as words and code blocks. A block opens with ``` at the start of a line (a word right
 * after it names the language) and closes on a later line that ends with ```; ```code``` on one line
 * is a block too. A fence that never closes isn't code: it stays as written.
 */
export function messageBlocks(body: string): Block[] {
  const lines = body.split('\n');
  const out: Block[] = [];
  let text: string[] = [];
  const flush = () => {
    if (text.length) out.push({ kind: 'text', text: text.join('\n') });
    text = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const open = FENCE_OPEN.exec(line);
    if (open) {
      const rest = open[1]!;
      const one = /^(.*\S.*?)```\s*$/.exec(rest);
      if (one) {
        flush();
        out.push({ kind: 'code', code: one[1]!, lang: null });
        continue;
      }
      const end = lines.findIndex((l, j) => j > i && /```\s*$/.test(l));
      if (end > i) {
        flush();
        const first = rest.trim();
        const lang = LANG.test(first) ? first : null;
        const code = lines.slice(i + 1, end);
        // "```some code" and "last line```": what shares a line with a fence is code too.
        if (first && !lang) code.unshift(rest);
        const last = lines[end]!.replace(/```\s*$/, '');
        if (last.trim()) code.push(last);
        out.push({ kind: 'code', code: code.join('\n'), lang });
        i = end;
        continue;
      }
    }
    text.push(line);
  }
  flush();
  return out;
}

/** Words with `inline code` set apart: single backticks on one line, not next to other backticks. */
export function inlineCode(text: string): Inline[] {
  const out: Inline[] = [];
  let at = 0;
  for (const m of text.matchAll(/(^|[^`])`([^`\n]+)`(?!`)/g)) {
    const start = (m.index ?? 0) + m[1]!.length;
    if (start > at) out.push({ kind: 'text', text: text.slice(at, start) });
    out.push({ kind: 'code', code: m[2]! });
    at = start + m[2]!.length + 2;
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
  return out;
}

/** A message in one line for lists and notifications: code markers dropped, spaces tidied. */
export function plainPreview(body: string): string {
  return body
    .replace(/^[ \t]*```[\w+#.-]*[ \t]*$/gm, ' ')
    .replace(/```/g, ' ')
    .replace(/(^|[^`])`([^`\n]+)`(?!`)/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
}

// --- links ------------------------------------------------------------------------------------------

const count = (s: string, c: string) => s.split(c).length - 1;

/**
 * A link without the sentence's punctuation after it; a closing bracket only goes when it closes
 * nothing in the link ("(see https://example.com/a_(b))"). As the server finds the link to preview.
 */
export function trimLink(raw: string): string {
  let url = raw;
  for (;;) {
    if (/[.,;:!?]$/.test(url)) url = url.slice(0, -1);
    else if (url.endsWith(')') && count(url, ')') > count(url, '(')) url = url.slice(0, -1);
    else if (url.endsWith(']') && count(url, ']') > count(url, '[')) url = url.slice(0, -1);
    else return url;
  }
}

// --- pasting code -------------------------------------------------------------------------------------

/** Lines that start like code: declarations, control flow, imports, tags, comments, SQL, a shell prompt. */
const CODE_START = new RegExp(
  [
    String.raw`(?:export\s+)?(?:default\s+)?(?:async\s+)?function\b`,
    String.raw`(?:export\s+)?(?:const|let|var)\s+[\w$[{]`,
    String.raw`(?:export\s+)?(?:default\s+)?(?:abstract\s+)?class\s+\w`,
    String.raw`import\s+[\w{*'"]`,
    String.raw`from\s+[\w.]+\s+import\s`,
    String.raw`export\s+(?:default\b|\{|\*)`,
    String.raw`return\b.*[;)\]}]$`,
    String.raw`(?:async\s+)?def\s+\w+\s*\(`,
    // Python's blocks: a header ending in a colon.
    String.raw`(?:if|elif|while|with)\s+.+:$`,
    String.raw`for\s+[\w, ]+\s+in\s+.+:$`,
    String.raw`(?:else|try|finally)\s*:$`,
    String.raw`except(?:\s+\w.*)?:$`,
    String.raw`class\s+\w+(?:\([^)]*\))?:$`,
    String.raw`(?:if|for|while|switch|catch)\s*\(`,
    String.raw`(?:try|else|finally|do)\s*\{`,
    String.raw`\}\s*(?:else|catch|finally|while)\b`,
    String.raw`(?:public|private|protected|static)\s+[\w<]`,
    String.raw`package\s+[\w.]+;?$`,
    String.raw`#include\s*[<"]`,
    String.raw`using\s+[\w.]+;`,
    String.raw`namespace\s+\w`,
    String.raw`(?:pub\s+)?fn\s+\w+\s*[<(]`,
    String.raw`func\s+(?:\([^)]*\)\s*)?\w*\s*\(`,
    String.raw`(?:type|struct|interface|enum)\s+\w+\s*[{=<]`,
    String.raw`(?:SELECT|INSERT\s+INTO|UPDATE\s+\w|DELETE\s+FROM|CREATE\s+(?:TABLE|INDEX|VIEW|UNIQUE)|ALTER\s+TABLE|DROP\s+TABLE|WHERE|FROM\s+\w|(?:LEFT\s+|INNER\s+)?JOIN\s|GROUP\s+BY|ORDER\s+BY|VALUES\s*\()`,
    String.raw`<\/?[a-zA-Z][\w-]*(?:\s[^>]*)?\/?>`,
    String.raw`\/\/`,
    String.raw`\/\*`,
    String.raw`\*\/`,
    String.raw`#!`,
    String.raw`\$\s+\w`,
    String.raw`(?:sudo|npm|npx|pnpm|yarn|git|docker|kubectl|curl|wget|pip3?|python3?|node|mkdir|chmod|apt-get|brew)\s+-{0,2}\w`
  ]
    .map((p) => `(?:${p})`)
    .join('|')
    .replace(/^/, '^\\s*(?:')
    .concat(')')
);
/** Lines that end like code: a statement, an opening brace, a lone closing bracket. */
const CODE_END = /(?:;|\{|\[|\(|=>\s*\{?|\)\s*\{)\s*$/;
const CODE_CLOSE = /^\s*[}\])](?:[\s;,)\]}]*|\s*(?:else|catch|finally)\b.*)$/;
/** Something code has and sentences rarely do. */
const CODE_HINTS: RegExp[] = [
  /===|!==|==|!=|<=|>=|=>|&&|\|\||::|\+=|-=|\*=|\/=|\+\+|<\//,
  /^\s*[\w$.[\]'"]+\s*[+\-*/%]?=\s*[^=\s]/,
  /[\w$]\([^()]*\)\s*;?$/,
  /^\s*"[^"]+"\s*:\s*\S/,
  /^\s*[\w-]+\s*:\s*[^:;]+;\s*$/,
  /^\s*return\s+\S/,
  // A block comment's middle lines (" * Adds two numbers.").
  /^\s+\*(?:\s|\/|$)/
];
/** A sentence: words ending in . ! or ?, with none of code's characters. */
const SENTENCE = /^[^{};=<>[\]]*\w[^{};=<>[\]]*[.!?]["')]?$/;
const LIST_ITEM = /^\s*(?:[-*•]|\d{1,3}[.)])\s+\S/;

/** Mostly letters, spaces and ordinary punctuation: words, even if a line ends in ";". */
function wordy(line: string): boolean {
  const t = line.trim();
  const words = t.match(/[\p{L}\p{N}\s,.'’!?:;-]/gu)?.length ?? 0;
  return words / Math.max(1, t.length) >= 0.88;
}

/**
 * Whether something pasted looks like code: several lines, most of them with code's signs (a
 * declaration or keyword at the start, a statement or brace at the end, operators, tags,
 * indentation), and hardly any that read as sentences or list items. Careful on purpose: wrapping
 * prose by mistake is worse than leaving code plain. Single lines are left alone.
 */
export function looksLikeCode(raw: string): boolean {
  const text = raw.replace(/\r\n?/g, '\n').replace(/\s+$/, '');
  if (!text || text.includes('```') || text.length > 40_000) return false;
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return false;
  let strong = 0;
  let hints = 0;
  let prose = 0;
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    const t = line.trim();
    if (CODE_START.test(line) || CODE_CLOSE.test(line) || (CODE_END.test(t) && !wordy(t))) strong++;
    else if (/^\s+\*(?:\s|\/|$)/.test(line)) hints++;
    else if (LIST_ITEM.test(line) || (SENTENCE.test(t) && t.split(/\s+/).length >= 4)) prose++;
    else if (CODE_HINTS.some((re) => re.test(line))) hints++;
  }
  const n = lines.length;
  const indented = lines.some((l) => /^(?:\t| {2,})\S/.test(l)) && lines.some((l) => /^\S/.test(l));
  const score = strong * 2 + hints + (indented && strong + hints > 0 ? 1 : 0) - prose * 2;
  return (
    (strong >= 2 || (strong >= 1 && hints >= 1)) &&
    strong + hints >= Math.ceil(n * 0.5) &&
    prose <= Math.floor(n * 0.25) &&
    score >= Math.max(3, Math.ceil(n * 0.6))
  );
}

/** Whether the caret is inside a code block already (an odd number of fences before it). */
export function insideFence(text: string, caret: number): boolean {
  return (text.slice(0, caret).match(/^[ \t]*```/gm) ?? []).length % 2 === 1;
}

/**
 * Code wrapped in a fence, to go between `before` and `after` in the box: on lines of its own,
 * its indentation kept, blank lines around it dropped.
 */
export function fenceCode(code: string, before = '', after = ''): string {
  const body = code
    .replace(/\r\n?/g, '\n')
    .replace(/^(?:[ \t]*\n)+/, '')
    .replace(/\s+$/, '');
  const lead = before === '' || before.endsWith('\n') ? '' : '\n';
  const tail = after === '' || after.startsWith('\n') ? '' : '\n';
  return `${lead}\`\`\`\n${body}\n\`\`\`${tail}`;
}

// --- a little colour ------------------------------------------------------------------------------

export type Token = { text: string; kind?: 'comment' | 'string' | 'number' | 'keyword' };

const KEYWORDS = new Set(
  (
    'function const let var return if else for while do switch case break continue new class extends implements import from export default async await ' +
    'try catch finally throw typeof instanceof in of null undefined true false this super yield delete void static public private protected interface type enum ' +
    'def lambda elif pass None True False self print raise except with as is not and or global nonlocal ' +
    'fn mut impl pub struct match use mod crate func go package defer chan select map range nil ' +
    'echo fi then esac done local export ' +
    'SELECT FROM WHERE AND OR NOT INSERT INTO VALUES UPDATE SET DELETE CREATE TABLE ALTER DROP JOIN LEFT RIGHT INNER OUTER ON GROUP BY ORDER LIMIT AS IS NULL PRIMARY KEY REFERENCES INDEX'
  ).split(' ')
);

/**
 * Code in a few colours: comments, strings, numbers and common keywords (JavaScript and TypeScript,
 * Python, Go, Rust, shell, SQL, C-like languages). A light touch, not a parser; long blocks stay plain.
 */
export function highlight(code: string): Token[] {
  if (code.length > 20_000) return [{ text: code }];
  const out: Token[] = [];
  let plain = '';
  const push = (text: string, kind?: Token['kind']) => {
    if (!kind) {
      plain += text;
      return;
    }
    if (plain) out.push({ text: plain });
    plain = '';
    out.push({ text, kind });
  };
  const take = (re: RegExp, i: number) => {
    re.lastIndex = i;
    return re.exec(code)?.[0] ?? null;
  };
  let i = 0;
  while (i < code.length) {
    const ch = code[i]!;
    const prev = i === 0 ? '\n' : code[i - 1]!;
    const next = code[i + 1] ?? '';
    const spaced = /\s/.test(prev);
    let m: string | null = null;
    let kind: Token['kind'];
    if (ch === '/' && next === '*') [m, kind] = [take(/\/\*[\s\S]*?(?:\*\/|$)/y, i), 'comment'];
    else if (ch === '/' && next === '/' && prev !== ':') [m, kind] = [take(/\/\/[^\n]*/y, i), 'comment'];
    else if (ch === '<' && code.startsWith('<!--', i)) [m, kind] = [take(/<!--[\s\S]*?(?:-->|$)/y, i), 'comment'];
    else if (ch === '#' && spaced && /^(?:\s|!|$)/.test(next)) [m, kind] = [take(/#[^\n]*/y, i), 'comment'];
    else if (ch === '-' && next === '-' && spaced && /\s/.test(code[i + 2] ?? '')) [m, kind] = [take(/--[^\n]*/y, i), 'comment'];
    else if (ch === '"') [m, kind] = [take(/"(?:\\.|[^"\\\n])*"?/y, i), 'string'];
    else if (ch === "'") [m, kind] = [take(/'(?:\\.|[^'\\\n])*'?/y, i), 'string'];
    else if (ch === '`') [m, kind] = [take(/`(?:\\[\s\S]|[^`\\])*`?/y, i), 'string'];
    else if (/[0-9]/.test(ch) && !/[\w$.]/.test(prev)) [m, kind] = [take(/(?:0[xX][0-9a-fA-F]+|\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/y, i), 'number'];
    else if (/[A-Za-z_$]/.test(ch) && !/[\w$]/.test(prev)) {
      m = take(/[A-Za-z_$][\w$]*/y, i);
      kind = m && KEYWORDS.has(m) ? 'keyword' : undefined;
    }
    if (m) {
      push(m, kind);
      i += m.length;
    } else {
      push(ch);
      i++;
    }
  }
  if (plain) out.push({ text: plain });
  return out;
}
